/**
 * Enforces the tenant's "Show Customer View to Shop Owners and Admins" setting (Admin → Branding) when it is OFF.
 *
 * ON (the default, and every tenant's behaviour before the setting existed): this renders nothing and makes no calls.
 *
 * OFF: shop owners and tenant admins are kept in their own console —
 *   - signed in as their staff account and on a storefront page (e.g. Back from the console): sent to their console;
 *   - signed in as their CUSTOMER account (the storefront login asks for CUSTOMER, and most staff also have a
 *     customer account on the same mobile): switched to their staff account through the same endpoint and hard
 *     navigation RoleSwitcher uses (see its iter66 note on why a soft navigation races the layout guards).
 * Someone holding both roles goes to the admin console. Customers, riders and zone admins are never touched.
 *
 * The switch always names the tenant it happens in (iter179). The server limits a switch to the caller's own tenant
 * and refuses any other, so this can never land someone in another store's console because the same mobile holds
 * the same staff role there too.
 */
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useTenant } from "../contexts/TenantContext";
import { STAFF_CONSOLES, consoleForRole, staffCustomerViewAllowed } from "../lib/staffCustomerView";

// Pages where switching mid-flow would be wrong: signing in, the permissions onboarding, the legal pages.
const SKIP_PREFIXES = ["/login", "/onboarding/", "/legal/"];

// The signed-in user object already checked. Keyed on the object, not the id: every sign-in produces a new one, so
// someone who logs out and back in within the same app run is checked again, while navigation inside one session
// never repeats the call.
let checkedUser = null;

export function _resetForTests() { checkedUser = null; }

export default function OwnerConsoleRedirect() {
    const { user } = useAuth();
    const { tenant } = useTenant();
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const current = useRef(user);
    current.current = user;
    const tenantSlug = useRef(tenant?.slug);
    tenantSlug.current = tenant?.slug;
    const blocked = !staffCustomerViewAllowed(tenant);

    useEffect(() => {
        if (!blocked || !user) return;
        if (SKIP_PREFIXES.some((p) => pathname.startsWith(p))) return;

        const own = consoleForRole(user.role);
        if (own) {
            navigate(own, { replace: true });
            return;
        }
        if (user.role !== "CUSTOMER" || checkedUser === user) return;
        checkedUser = user;
        // Not cancelled on navigation — a route change mid-request must not lose the check. It only stands down if
        // the account itself changed meanwhile (e.g. signed out).
        (async () => {
            try {
                const { data } = await api.get("/auth/my-roles");
                const mine = (data?.roles || []).filter((r) => !r.tenant_id || r.tenant_id === user.tenant_id);
                const held = new Set(mine.map((r) => r.role));
                const target = STAFF_CONSOLES.find(([role]) => held.has(role));
                if (!target || current.current !== user) return;
                // The tenant of the role row itself (what RoleSwitcher sends), else the tenant the server resolved
                // for this session, else the storefront being browsed.
                const slug = mine.find((r) => r.role === target[0])?.tenant_slug || data?.tenant_slug || tenantSlug.current;
                await api.post("/auth/switch-role", { role: target[0], tenant_slug: slug || undefined });
                // replace, not assign: Back must not lead straight back into the storefront.
                if (current.current === user) window.location.replace(target[1]);
            } catch {
                // Stay where we are: the customer view still works, exactly as before this existed.
            }
        })();
    }, [blocked, user, pathname, navigate]);

    return null;
}
